import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useI18n } from "@/lib/i18n";
import { ING_IMG, ING_PHOTO } from "@/lib/content";
import { Reveal, SplitText } from "@/components/fx/Motion";

const EASE = [0.22, 1, 0.36, 1];

const Ingredient = ({ ing, i }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [30 * ((i % 3) - 1), -30 * ((i % 3) - 1)]);
  return (
    <motion.div ref={ref} data-testid={`story-ingredient-${i}`} className="group text-center" initial={{ opacity: 0, y: 60 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }} transition={{ duration: 1, ease: EASE, delay: (i % 3) * 0.12 }}>
      <motion.div style={{ y }} className="relative mx-auto h-36 w-36 sm:h-44 sm:w-44">
        <div className="absolute inset-0 rounded-full bg-[#c9a55c]/25 blur-2xl scale-90 group-hover:scale-110 transition-transform duration-700" />
        <div className="relative h-full w-full rounded-full overflow-hidden border border-[#c9a55c]/40 group-hover:border-[#c9a55c] transition-colors duration-500">
          <img src={ING_PHOTO[ing.photo] || ING_IMG[ing.kind] || ING_IMG.botanical} alt={ing.name} loading="lazy" data-testid={`ingredient-photo-${ing.photo || ing.kind}`} className="h-full w-full object-cover scale-110 group-hover:scale-125 transition-transform duration-[1.2s] ease-out" />
        </div>
        <span className="absolute -top-1 -right-1 h-9 w-9 rounded-full bg-gold text-[#0b0a08] text-xs font-bold flex items-center justify-center">{String(i + 1).padStart(2, "0")}</span>
      </motion.div>
      <p className="font-display text-xl mt-6">{ing.name}</p>
      <p className="text-sm text-white/60 mt-2 max-w-[240px] mx-auto leading-relaxed">{ing.role}</p>
    </motion.div>
  );
};

const Benefits = ({ items }) => (
  <div className="grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
    {items.map((b, i) => (
      <motion.div key={i} data-testid={`story-benefit-${i}`} className="bg-[#0b0a08] p-7 lg:p-9 group relative overflow-hidden"
        initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: i * 0.12 }}>
        <motion.span className="absolute left-0 top-0 h-full w-[2px] bg-gold origin-top" initial={{ scaleY: 0 }} whileInView={{ scaleY: 1 }} viewport={{ once: true }} transition={{ duration: 1, delay: 0.3 + i * 0.12, ease: EASE }} />
        <p className="font-display text-5xl text-gold/30 group-hover:text-gold transition-colors duration-500">{String(i + 1).padStart(2, "0")}</p>
        <p className="font-display text-2xl mt-3">{b.title}</p>
        <p className="text-sm text-white/60 mt-3 leading-relaxed">{b.text}</p>
      </motion.div>
    ))}
  </div>
);

export const StoryIntro = ({ story }) => (
  <div data-testid="story-intro" className="mt-8">
    <p className="font-display italic text-2xl text-[#e3cb94] leading-snug">{story.tagline}</p>
    <p className="text-white/70 mt-4 leading-relaxed">{story.intro}</p>
    {!!story.facts?.length && (
      <div className="flex flex-wrap gap-2 mt-5">
        {story.facts.map((f, i) => <span key={i} data-testid={`story-fact-${i}`} className="px-3 py-1.5 border border-[#c9a55c]/40 text-[11px] uppercase tracking-[0.08em] text-[#e3cb94]">{f}</span>)}
      </div>
    )}
  </div>
);

export const ProductStory = ({ story, descriptionHtml }) => {
  const { t } = useI18n();
  return (
    <div data-testid="product-story">
      {story && (
        <>
          <section className="max-w-7xl mx-auto px-5 lg:px-10 py-20 grid lg:grid-cols-[0.8fr_1.2fr] gap-10 lg:gap-16">
            <div>
              <p className="eyebrow mb-4">{t("story_benefits")}</p>
              <h2 className="font-display text-3xl lg:text-5xl leading-tight"><SplitText text={story.tagline} /></h2>
            </div>
            <Benefits items={story.benefits || []} />
          </section>
          <section data-testid="story-ingredients" className="relative py-24 bg-[#0e0d0b] border-y border-white/5 overflow-hidden grain">
            <div className="absolute -left-40 top-10 h-96 w-96 rounded-full bg-[#c9a55c]/10 blur-[120px]" />
            <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
              <div className="text-center max-w-xl mx-auto mb-16">
                <p className="eyebrow mb-4">{t("story_ingredients_eyebrow")}</p>
                <h2 className="font-display text-3xl lg:text-5xl"><SplitText text={t("story_ingredients")} /></h2>
                <Reveal delay={0.2}><p className="text-white/60 mt-4">{t("story_ingredients_text")}</p></Reveal>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-14">
                {(story.ingredients || []).map((ing, i) => <Ingredient key={i} ing={ing} i={i} />)}
              </div>
            </div>
          </section>
          {!!story.ritual?.length && (
            <section data-testid="story-ritual" className="max-w-5xl mx-auto px-5 lg:px-10 py-20">
              <p className="eyebrow mb-4 text-center">{t("story_ritual")}</p>
              <div className="grid md:grid-cols-3 gap-8 mt-10 relative">
                <motion.div className="hidden md:block absolute top-6 left-[16%] right-[16%] h-px bg-[#c9a55c]/40 origin-left" initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} transition={{ duration: 1.6, ease: EASE }} />
                {story.ritual.map((r, i) => (
                  <Reveal key={i} delay={0.2 + i * 0.2} className="text-center relative">
                    <span className="mx-auto h-12 w-12 rounded-full border border-[#c9a55c] bg-[#0b0a08] flex items-center justify-center font-display text-gold text-lg relative">{i + 1}</span>
                    <p className="text-sm text-white/75 mt-5 leading-relaxed max-w-[260px] mx-auto">{r}</p>
                  </Reveal>
                ))}
              </div>
            </section>
          )}
        </>
      )}
      <section className="max-w-4xl mx-auto px-5 lg:px-10 pb-10">
        <Accordion type="single" collapsible defaultValue={story ? undefined : "details"}>
          <AccordionItem value="details" className="border-white/10">
            <AccordionTrigger data-testid="story-details-toggle" className="font-display text-lg hover:no-underline">{t("story_details")}</AccordionTrigger>
            <AccordionContent>
              <div data-testid="product-description" className="rte text-sm" dangerouslySetInnerHTML={{ __html: descriptionHtml }} />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </section>
    </div>
  );
};
